import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Dialog, DialogBackdrop, DialogPanel, Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import clsx from "clsx";
import {
  LayoutDashboard,
  BookOpen,
  Users,
  Calendar,
  MessageSquare,
  Shield,
  LogOut,
  Crown,
  Menu as MenuIcon,
  FlaskConical,
  Wrench,
  X,
  Building2,
  LineChart,
  MessageSquarePlus,
  Sun,
  Moon,
  User,
  GraduationCap,
} from "lucide-react";

import { useAuth } from "../../contexts/AuthContext";
import { useNotifications } from "../../contexts/NotificationContext";
import { useAdminBadge } from "../../hooks/useAdminBadge";
import { useSidebarContext } from "../../contexts/SidebarContext";
import { useFeatureAccess } from "../../hooks/useFeatureAccess";
import { getImageUrl } from "../../utils/imageUtils";
import { UserAvatar } from "../ui/UserAvatar";
import { AdminCardNavTW } from "./AdminCardNavTW";
import { FeedbackSubmissionPanelTW } from "../feedback/FeedbackSubmissionPanelTW";

// ============================================================================
// TAILWIND-ONLY SIDEBAR
// Keine externe CSS-Datei - alle Styles sind inline Tailwind-Klassen
// ============================================================================

// Sidebar Dimension (für left-Offset der Flyouts)
const SIDEBAR_WIDTH = 220;

// Logo SVG inline (yellow fox)
const LogoSvg: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 250 250" xmlns="http://www.w3.org/2000/svg" className={className}>
    <g transform="matrix(0.46323,0,0,0.46323,173.686,80.7525)">
      <path
        d="M0,191.039C-18.392,205.266 -36.578,220.573 -53.061,237.723L-53.061,17.543L-26.531,17.543L0,-11.368L0,191.039ZM-78.571,267.969C-88.993,282.282 -98.042,297.816 -105.101,314.886C-112.162,297.814 -121.208,281.911 -131.631,267.082L-131.631,83.041L-105.101,83.041L-78.571,54.129L-78.571,267.969ZM-157.142,235.521C-173.625,217.529 -191.81,201.394 -210.202,186.894L-210.202,138.601L-183.672,138.601L-157.142,109.69L-157.142,235.521ZM120.967,-103.368C124.374,-77.815 126.929,43.987 126.929,43.987C185.701,-65.039 136.299,-172.361 136.299,-172.361L-3.391,-61.632L-206.811,-61.632L-346.501,-172.361C-346.501,-172.361 -395.904,-65.039 -337.132,43.987C-337.132,43.987 -334.577,-77.815 -331.169,-103.368L-272.398,-13.933L-346.501,122.35C-346.501,122.35 -161.819,226.265 -105.101,363.4C-48.383,226.265 136.299,122.35 136.299,122.35L62.195,-13.933L120.967,-103.368Z"
        style={{ fillRule: "nonzero", fill: "rgb(241, 196, 15)" }}
      />
    </g>
  </svg>
);

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface SidebarItemProps {
  to?: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  badge?: number;
  badgeColor?: string;
  disabled?: boolean;
  comingSoon?: boolean;
  /** Zeigt ein "Beta"-Badge neben dem Menüeintrag */
  betaBadge?: boolean;
  onClick?: () => void;
  /** Verhindert das Schließen der Sidebar auf Mobile (z.B. für Flyouts) */
  keepSidebarOpen?: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  to,
  icon,
  children,
  badge,
  disabled,
  comingSoon,
  betaBadge,
  onClick,
  keepSidebarOpen = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile, closeMobileSidebar } = useSidebarContext();
  const isActive = to
    ? location.pathname === to || (to !== "/" && location.pathname.startsWith(to))
    : false;

  // Dauer der Mobile-Dialog-Schließanimation in ms (siehe DialogPanel style.transition)
  const MOBILE_CLOSE_ANIM_MS = 300;

  const baseClasses = `
    group relative flex items-center gap-3 rounded-lg
    text-sm font-medium cursor-pointer
    border-none w-full text-left
    transition-colors duration-150 ease-out
    focus:outline-none
  `;

  const stateClasses = isActive
    ? "bg-brand text-gray-900 border-l-[3px] border-brand shadow-sm"
    : "text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 border-l-[3px] border-transparent";

  const disabledClasses = disabled ? "opacity-50 pointer-events-none" : "";

  const className = `${baseClasses} ${stateClasses} ${disabledClasses}`.trim();
  const baseStyle = { padding: '10px 14px' };

  const content = (
    <>
      <span
        className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? "text-gray-900" : "text-gray-500 dark:text-gray-300"}`}
      >
        {icon}
      </span>
      <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">
        {children}
      </span>
      {comingSoon && (
        <span className="text-[9px] font-semibold uppercase tracking-wide text-gray-500 bg-gray-200 dark:bg-white/5 px-1.5 py-0.5 rounded ml-auto">
          Bald
        </span>
      )}
      {betaBadge && (
        <span className={`
          text-[9px] font-semibold uppercase tracking-wide
          px-1.5 py-0.5 rounded ml-auto
          ${isActive
            ? "bg-gray-900/20 text-gray-900"
            : "bg-sky-500/15 text-sky-700 dark:text-sky-400"
          }
        `}>
          Beta
        </span>
      )}
      {badge !== undefined && badge > 0 && (
        <span className="min-w-[20px] h-5 px-1.5 text-[10px] font-semibold bg-brand text-gray-900 rounded-full flex items-center justify-center ml-auto flex-shrink-0">
          {badge}
        </span>
      )}
    </>
  );

  if (disabled) {
    return (
      <button className={className} style={baseStyle} disabled>
        {content}
      </button>
    );
  }

  if (onClick) {
    return (
      <button
        className={className}
        style={baseStyle}
        onClick={() => {
          onClick();
          if (isMobile && !keepSidebarOpen) closeMobileSidebar();
        }}
      >
        {content}
      </button>
    );
  }

  // Bei Navigation auf Mobile: Erst Close-Animation abspielen, dann navigieren.
  // Sonst wechselt die Route instant und die 300ms-Slide-Animation wird unsichtbar.
  const handleLinkClick = (e: React.MouseEvent) => {
    if (isMobile && !keepSidebarOpen && to) {
      e.preventDefault();
      closeMobileSidebar();
      setTimeout(() => navigate(to), MOBILE_CLOSE_ANIM_MS);
    }
  };

  return (
    <Link to={to || "#"} className={className} style={baseStyle} onClick={handleLinkClick}>
      {content}
    </Link>
  );
};

interface SidebarSectionProps {
  label: string;
  children: React.ReactNode;
}

const SidebarSection: React.FC<SidebarSectionProps> = ({ label, children }) => (
  <div className="flex flex-col gap-1 mb-2">
    <span className="block px-3.5 py-2 text-[10px] font-semibold uppercase tracking-widest text-gray-500">
      {label}
    </span>
    {children}
  </div>
);

// ============================================================================
// MAIN SIDEBAR COMPONENT
// ============================================================================

export interface SidebarTWProps {
  className?: string;
}

export const SidebarTW: React.FC<SidebarTWProps> = ({ className = "" }) => {
  const { user, isAuthenticated, logout, originalRole, setDemoRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [logoHoverState, setLogoHoverState] = useState<"idle" | "entering" | "leaving">("idle");
  const logoAnimatingRef = useRef(false);
  const { counts } = useNotifications();

  // Premium-Support-Chat Badge: Anzahl Kunden mit ungelesenen Nachrichten.
  // Nur für Admins aktiv. Live-Updates via WebSocket (siehe useAdminBadge).
  const adminBadge = useAdminBadge({ enabled: originalRole === 'admin' });
  const { hasAccess: hasGmbHAccess, featureStatus: gmbhStatus } = useFeatureAccess('gmbh_module');
  const { hasAccess: hasTermineAccess, featureStatus: termineStatus } = useFeatureAccess('termine_module');
  const { hasAccess: hasBlogAccess, featureStatus: blogStatus } = useFeatureAccess('blog_module');
  const { hasAccess: hasFcmsAccess, featureStatus: fcmsStatus } = useFeatureAccess('fcms_module');
  const { hasAccess: hasAcademyAccess, featureStatus: academyStatus } = useFeatureAccess('academy');
  const [adminFlyoutOpen, setAdminFlyoutOpen] = useState(false);
  const [gmbhFlyoutOpen, setGmbhFlyoutOpen] = useState(false);
  const [feedbackPanelOpen, setFeedbackPanelOpen] = useState(false);

  // User-Menu (Desktop-Footer): öffnet sich bei Hover/Focus über dem Avatar-Block.
  // Ref + Timeout-Handling sorgt dafür, dass das Menü nicht sofort schließt,
  // wenn die Maus vom Trigger ins Flyout wandert.
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuContainerRef = useRef<HTMLDivElement>(null);
  const userMenuCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelCloseUserMenu = () => {
    if (userMenuCloseTimeoutRef.current) {
      clearTimeout(userMenuCloseTimeoutRef.current);
      userMenuCloseTimeoutRef.current = null;
    }
  };

  const openUserMenu = () => {
    cancelCloseUserMenu();
    setUserMenuOpen(true);
  };

  const scheduleCloseUserMenu = () => {
    cancelCloseUserMenu();
    userMenuCloseTimeoutRef.current = setTimeout(() => setUserMenuOpen(false), 120);
  };

  // Theme State für Mobile/Tablet Toggle
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("rf-theme");
    return saved ? saved === "dark" : true;
  });

  // Theme-Änderungen synchronisieren
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
      document.documentElement.classList.remove("light");
    } else {
      document.documentElement.classList.remove("dark");
      document.documentElement.classList.add("light");
    }
    localStorage.setItem("rf-theme", isDarkMode ? "dark" : "light");
  }, [isDarkMode]);

  // Sync mit anderen Tabs/Komponenten (z.B. TWThemeToggle)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "rf-theme") {
        setIsDarkMode(e.newValue === "dark");
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Sidebar State vom Context (nur Mobile-Overlay-Verhalten).
  // HINWEIS: isMobile bleibt im Context verfügbar für Flyouts und andere Konsumenten.
  // In dieser Komponente entscheidet jedoch CSS (xl:hidden / hidden xl:flex) über
  // die Sichtbarkeit von Mobile- vs. Desktop-Sidebar. Headless UI Dialog übernimmt
  // Animation, Body-Scroll-Lock und Backdrop-Close automatisch.
  const { isMobile, isMobileOpen, openMobileSidebar, closeMobileSidebar } = useSidebarContext();

  // Swipe-to-close für Mobile-Dialog: Start-Koordinaten beim Touch-Start merken.
  // Headless UI Dialog hat keinen eingebauten Swipe; wir ergänzen das hier lokal.
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    if (t) swipeStartRef.current = { x: t.clientX, y: t.clientY };
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!swipeStartRef.current) return;
    const t = e.touches[0];
    if (!t) return;
    const deltaX = t.clientX - swipeStartRef.current.x;
    const deltaY = Math.abs(t.clientY - swipeStartRef.current.y);
    // Nach rechts wischen (dx > 50px, dominant horizontal) schließt die Sidebar
    if (deltaX > 50 && deltaY < deltaX) {
      swipeStartRef.current = null;
      closeMobileSidebar();
    }
  };

  const handleTouchEnd = () => {
    swipeStartRef.current = null;
  };

  const isAdmin = originalRole === "admin";
  // Demo-Modus aktiv: Admin sieht die App wie ein Kunde/Beta (aber Admin-Sektion bleibt sichtbar)
  const isDemoActive = isAdmin && user?.role !== "admin";

  // Aktuelle Rolle für die Badge auf dem Avatar-Menü. Berücksichtigt
  // Demo-Modus: wenn Admin gerade als Beta/Kunde "browst", zeigt die Badge
  // die Demo-Rolle (weil user.role dann entsprechend umgestellt ist).
  const currentRole: 'admin' | 'beta' | 'kunde' =
    user?.role === 'admin'
      ? 'admin'
      : user?.isBetaTester
        ? 'beta'
        : 'kunde';
  const roleBadgeLabel = currentRole === 'admin' ? 'Admin' : currentRole === 'beta' ? 'Beta' : 'Kunde';
  const RoleBadgeIcon = currentRole === 'admin' ? Wrench : currentRole === 'beta' ? FlaskConical : Crown;

  // Cleanup: ausstehenden Close-Timeout bei Unmount verwerfen
  useEffect(() => {
    return () => {
      if (userMenuCloseTimeoutRef.current) {
        clearTimeout(userMenuCloseTimeoutRef.current);
      }
    };
  }, []);

  // Logo-Hover Animation
  const handleLogoMouseEnter = () => {
    if (logoAnimatingRef.current) return;
    logoAnimatingRef.current = true;
    setLogoHoverState("entering");

    setTimeout(() => {
      setLogoHoverState("leaving");
      setTimeout(() => {
        setLogoHoverState("idle");
        logoAnimatingRef.current = false;
      }, 500);
    }, 500 + 2000);
  };

  // Total admin notifications
  const totalAdminNotifications =
    counts.system.alerts +
    counts.betaManagement.pendingFeedback +
    counts.betaManagement.newUsers +
    counts.deployment.available;

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // Admin categories for flyout
  const adminCategories = [
    {
      label: "Dashboard",
      sectionBadge: counts.betaManagement.newUsers || undefined,
      links: [
        { label: "Übersicht", href: "/admin-dashboard" },
        {
          label: "Benutzer",
          href: "/admin-dashboard/users",
          badge: counts.betaManagement.newUsers || undefined,
          badgeColor: "#F7931A",
        },
      ],
    },
    {
      label: "Finanzen",
      sectionBadge: counts.revocations.open || undefined,
      links: [
        { label: "Umsatzübersicht", href: "/admin-dashboard/revenue" },
        // "Rechnungen" + "Rechnungseinstellungen" wurden ins GmbH-Panel
        // verschoben (siehe /accounting/invoices und /accounting/company-info).
        // Die alten Routes leiten weiter (siehe App.tsx).
        {
          label: "Kündigungen & Widerrufe",
          href: "/admin-dashboard/revocations",
          badge: counts.revocations.open || undefined,
          badgeColor: "#F7931A",
        },
        { label: "Preise & Services", href: "/admin-dashboard/settings/pricing" },
        { label: "Rabatte & Coupons", href: "/admin-dashboard/settings/coupons" },
      ],
    },
    {
      label: "Server & Tools",
      sectionBadge: (counts.deployment.available + counts.system.alerts) || undefined,
      links: [
        {
          label: "Server",
          href: "/admin-dashboard/system/overview",
          badge: (counts.deployment.available + counts.system.alerts) || undefined,
          badgeColor: counts.system.alerts > 0 ? "#dc2626" : "#F7931A",
        },
        { label: "Datenbank & Tools", href: "/admin-dashboard/system/databases" },
      ],
    },
    {
      label: "Blog",
      sectionBadge: adminBadge.count || undefined,
      links: [
        { label: "Neuer Beitrag", href: "/admin-dashboard/blog/new" },
        { label: "Alle Beiträge", href: "/admin-dashboard/blog/posts" },
        { label: "Media-Bibliothek", href: "/admin-dashboard/blog/media" },
        { label: "RSS-Feeds", href: "/admin-dashboard/blog/rss-feeds" },
        {
          label: "Priority Support",
          href: "/admin-dashboard/blog/premium-support",
          badge: adminBadge.count || undefined,
          badgeColor: "#F1C40F",
        },
      ],
    },
    {
      label: "Termine",
      links: [
        { label: "Termin-Management", href: "/admin-dashboard/appointments" },
      ],
    },
    {
      label: "FCMS",
      links: [
        { label: "Dashboard", href: "/admin-dashboard/fcms/dashboard" },
        { label: "YouTube-Matching", href: "/admin-dashboard/fcms/youtube-matching" },
        { label: "Asset Management", href: "/admin-dashboard/asset-management" },
      ],
    },
    {
      label: "Community",
      sectionBadge: counts.betaManagement.pendingFeedback || undefined,
      links: [
        { label: "Affiliates", href: "/admin-dashboard/affiliates" },
        { label: "Beta-Programm", href: "/admin-dashboard/beta" },
        {
          label: "Feedback Board",
          href: "/admin-dashboard/feedback",
          badge: counts.betaManagement.pendingFeedback || undefined,
          badgeColor: "#F7931A",
        },
        { label: "Produkt-Updates", href: "/admin-dashboard/announcements" },
      ],
    },
    {
      label: "Einstellungen",
      links: [
        { label: "E-Mail Vorschau", href: "/admin-dashboard/email-preview" },
        { label: "E-Mail Warteschlange", href: "/admin-dashboard/email-queue" },
        { label: "UI Styleguide", href: "/admin-dashboard/styleguide" },
      ],
    },
  ];

  // GmbH categories for flyout - nur für Beta-Tester oder Admins sichtbar (im Demo-Modus: Feature-Access entscheidet)
  const showGmbHSection = hasGmbHAccess || (isAdmin && !isDemoActive);

  const gmbhCategories = [
    {
      label: "Übersicht",
      links: [
        { label: "Cockpit", href: "/gmbh/dashboard" },
      ],
    },
    {
      label: "Investments",
      links: [
        { label: "Wertpapiere", href: "/gmbh/portfolio" },
        { label: "Krypto", href: "/gmbh/krypto" },
      ],
    },
    {
      label: "Buchhaltung",
      links: [
        // Hub-basierte Navigation: jeder Eintrag öffnet eine Seite mit Sub-Tabs
        { label: "Liquidität", href: "/accounting/cash-position" },
        { label: "Buchungen", href: "/accounting/bookings" },
        { label: "Berichte", href: "/accounting/reports" },
        { label: "Periode & Abschluss", href: "/accounting/periods" },
        { label: "Rechnungen", href: "/accounting/invoices" },
      ],
    },
    {
      label: "Verwaltung",
      links: [
        { label: "API & Webhook", href: "/gmbh/connections" },
        { label: "Verfahrensdokumentation", href: "/accounting/process-docs" },
        { label: "Audit-Log", href: "/accounting/audit-log" },
        { label: "Plausibilitäts-Dismissals", href: "/accounting/plausibility-dismissals" },
      ],
    },
    {
      label: "Test-Werkzeuge",
      links: [
        { label: "Test-Mandant verwalten", href: "/accounting/test-tenant" },
        { label: "Vorjahres-Migration", href: "/accounting/migration" },
      ],
    },
  ];

  if (!isAuthenticated || !user) {
    return null;
  }

  // Desktop-Sidebar-Breite (für left-Offset der Flyouts)
  const sidebarWidth = SIDEBAR_WIDTH;

  /**
   * Rendert den Sidebar-Inhalt (Logo, Navigation, Footer mit User-Area).
   * Wird in zwei Kontexten gerendert:
   *  - Mobile: innerhalb eines Headless UI <Dialog> (isMobileView=true)
   *  - Desktop: innerhalb eines persistenten <aside> (isMobileView=false)
   *
   * Die Sidebar ist in beiden Kontexten immer voll ausgeklappt (220px) -
   * es gibt keinen Collapse-Mode mehr.
   */
  const renderSidebarBody = (isMobileView: boolean) => {
    return (
      <>
        {/* Header: Logo + Avatar (Mobile) bzw. Theme-Toggle (Desktop) */}
        <div className="relative flex items-center h-16 box-border justify-between pl-4 pr-3">
          <Link
            to="/"
            className="flex items-center gap-2 no-underline text-gray-900 dark:text-gray-100 overflow-hidden min-w-0"
            onMouseEnter={handleLogoMouseEnter}
            onClick={(e) => {
              if (isMobileView) {
                e.preventDefault();
                closeMobileSidebar();
                setTimeout(() => navigate('/'), 300);
              }
            }}
          >
            <div className="relative w-7 h-7 flex-shrink-0 overflow-hidden drop-shadow-[1px_1px_1px_rgba(0,0,0,0.4)] dark:drop-shadow-none">
              <LogoSvg className="absolute inset-0 w-7 h-7" />
              <img
                src={getImageUrl("/images/branding/rf-fux-germany.svg")}
                alt=""
                className={`
                  absolute inset-0 w-7 h-7
                  ${logoHoverState === "idle" ? "[clip-path:polygon(0_0,0_0,0_100%,0_100%)]" : ""}
                  ${logoHoverState === "entering" ? "[clip-path:polygon(0_0,100%_0,100%_100%,0_100%)] transition-[clip-path] duration-500 ease-out" : ""}
                  ${logoHoverState === "leaving" ? "[clip-path:polygon(100%_0,100%_0,100%_100%,100%_100%)] transition-[clip-path] duration-500 ease-out" : ""}
                `}
              />
            </div>
            <span
              className="text-sm font-semibold whitespace-nowrap [text-shadow:1px_1px_1px_rgba(0,0,0,0.4)] dark:[text-shadow:none]"
              style={{ color: '#F1C40F' }}
            >
              RENDITEFUCHS
            </span>
          </Link>

          {/* Rechts: Auf Mobile -> Avatar-Button mit User-Menu-Dropdown.
              Auf Desktop existiert nur noch der neue Footer mit Flyout (unten). */}
          <div className="flex items-center gap-1">
              {/* Mobile: Avatar-Button oeffnet User-Menu-Dropdown */}
              {isMobileView && (
                <Menu>
                  <MenuButton
                    aria-label="Benutzer-Menü öffnen"
                    className="rounded-full focus:outline-none cursor-pointer data-focus:ring-2 data-focus:ring-brand data-focus:ring-offset-2 data-focus:ring-offset-gray-50 dark:data-focus:ring-offset-gray-900 transition-all"
                  >
                    <UserAvatar user={user} size="sm" />
                  </MenuButton>

                  <MenuItems
                    transition
                    anchor="bottom end"
                    className="glass-surface-soft min-w-[240px] origin-top-right rounded-2xl p-1 text-sm transition duration-100 ease-out [--anchor-gap:--spacing(2)] focus:outline-none data-closed:scale-95 data-closed:opacity-0 z-[10000] !overflow-visible"
                  >
                    {/* Rollen-Badge: aufgeklebt oben rechts */}
                    <span className={`role-badge role-badge-${currentRole}`}>
                      <RoleBadgeIcon className="size-[10px]" />
                      {roleBadgeLabel}
                    </span>

                    {/* Header: Name + Email links, Theme-Toggle rechts.
                        items-center -> Theme-Button vertikal zentriert zum
                        2-zeiligen Name+Email-Block.
                        Der Theme-Button ist KEIN MenuItem -> Klick schliesst das
                        Dropdown nicht (Headless UI schliesst nur bei MenuItem-Click). */}
                    <div className="flex items-center gap-2 px-3 py-2">
                      <div className="flex-1 min-w-0" data-sensitive>
                        <p className="font-semibold truncate">
                          {user.firstName || "User"} {user.lastName || ""}
                        </p>
                        <p className="text-gray-500 dark:text-white/60 text-xs truncate">
                          {user.email || user.username || ""}
                        </p>
                      </div>
                      {!location.pathname.startsWith('/fcms') && (
                        <button
                          onClick={() => setIsDarkMode(!isDarkMode)}
                          aria-label={isDarkMode ? 'Zum Light-Mode wechseln' : 'Zum Dark-Mode wechseln'}
                          title={isDarkMode ? 'Light-Mode' : 'Dark-Mode'}
                          className={`rounded-full p-1.5 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors cursor-pointer flex-shrink-0 ${isDarkMode ? 'text-brand' : 'text-gray-500 hover:text-gray-900 dark:text-white/60 dark:hover:text-white'}`}
                        >
                          {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
                        </button>
                      )}
                    </div>

                    <div className="mx-3 my-1 h-px bg-black/10 dark:bg-white/10" />

                    {/* Mein Profil */}
                    <MenuItem>
                      <Link
                        to="/profil"
                        onClick={(e) => {
                          e.preventDefault();
                          closeMobileSidebar();
                          setTimeout(() => navigate('/profil'), 300);
                        }}
                        className="group flex w-full items-center gap-3 rounded-lg px-3 py-2 data-focus:bg-gray-100 dark:data-focus:bg-white/10 cursor-pointer"
                      >
                        <User className="size-4 text-gray-500 dark:text-white/60 group-data-focus:text-gray-900 dark:group-data-focus:text-white" />
                        Mein Profil
                      </Link>
                    </MenuItem>

                    {/* Rollen-Switcher (nur Admin, Demo-Modus) */}
                    {isAdmin && (
                      <>
                        <div className="mx-3 my-1 h-px bg-black/10 dark:bg-white/10" />
                        {([
                          { id: 'admin' as const, label: 'Als Admin anzeigen', icon: <Wrench className="size-4" />, color: 'text-amber-600 dark:text-amber-400' },
                          { id: 'beta' as const, label: 'Als Beta anzeigen', icon: <FlaskConical className="size-4" />, color: 'text-sky-600 dark:text-sky-400' },
                          { id: 'user' as const, label: 'Als Kunde anzeigen', icon: <Crown className="size-4" />, color: 'text-lime-600 dark:text-lime-400' },
                        ]).map((option) => {
                          const isActive = (option.id === 'admin' && user.role === 'admin')
                            || (option.id === 'beta' && user.role === 'user' && user.isBetaTester)
                            || (option.id === 'user' && user.role === 'user' && !user.isBetaTester);
                          return (
                            <MenuItem key={option.id}>
                              <button
                                onClick={() => setDemoRole(option.id)}
                                className={`group flex w-full items-center gap-3 rounded-lg px-3 py-2 data-focus:bg-gray-100 dark:data-focus:bg-white/10 cursor-pointer ${option.color}`}
                              >
                                {option.icon}
                                <span className="flex-1 text-left">{option.label}</span>
                                {isActive && <span className="text-brand">&#10003;</span>}
                              </button>
                            </MenuItem>
                          );
                        })}
                      </>
                    )}

                    <div className="mx-3 my-1 h-px bg-black/10 dark:bg-white/10" />

                    {/* Abmelden */}
                    <MenuItem>
                      <button
                        onClick={() => {
                          closeMobileSidebar();
                          handleLogout();
                        }}
                        className="group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-red-600 dark:text-red-400 data-focus:bg-red-50 dark:data-focus:bg-red-500/10 cursor-pointer"
                      >
                        <LogOut className="size-4" />
                        Abmelden
                      </button>
                    </MenuItem>
                  </MenuItems>
                </Menu>
              )}

            </div>

        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-2 flex flex-col gap-1 scrollbar-styled">
            <SidebarItem
              to="/dashboard"
              icon={<LayoutDashboard size={18} />}
            >
              Dashboard
            </SidebarItem>

            {/* Academy: über Feature-Flag (academy-Parent) gesteuert.
                development -> nur Admin; beta -> Beta-Tester; live -> alle.
                Bezahlung/Zugang pro Modul regelt access_grants beim Klick. */}
            {(hasAcademyAccess || (isAdmin && !isDemoActive)) && (
              <SidebarItem
                to="/academy"
                icon={<GraduationCap size={18} />}
                betaBadge={academyStatus === 'beta'}
              >
                Academy
              </SidebarItem>
            )}

            {(hasBlogAccess || (isAdmin && !isDemoActive)) && (
              <SidebarItem
                to="/blog"
                icon={<BookOpen size={18} />}
                betaBadge={blogStatus === 'beta'}
              >
                Blog
              </SidebarItem>
            )}

            {(hasFcmsAccess || (isAdmin && !isDemoActive)) && (
              <SidebarItem
                to="/fcms"
                icon={<LineChart size={18} />}
                betaBadge={fcmsStatus === 'beta'}
              >
                FCMS
              </SidebarItem>
            )}

            {(hasTermineAccess || (isAdmin && !isDemoActive)) && (
              <SidebarItem
                to="/termine"
                icon={<Calendar size={18} />}
                betaBadge={termineStatus === 'beta'}
              >
                Termine
              </SidebarItem>
            )}

            {/* Trennlinie vor Feedback */}
            <div className="h-px mx-2 my-1 bg-black/10 dark:bg-white/10" />

            <div className="relative group/feedback flex items-center">
              <SidebarItem
                to="/feedback"
                icon={<MessageSquare size={18} />}
              >
                Feedback
              </SidebarItem>
              {/* Plus-Button zum direkten Öffnen des Feedback-Panels */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (isMobileView) closeMobileSidebar();
                  setFeedbackPanelOpen(true);
                }}
                className={`
                  absolute right-1.5 top-1/2 -translate-y-1/2
                  w-6 h-6 flex items-center justify-center rounded-md
                  text-gray-500 dark:text-gray-400
                  hover:bg-gray-300 dark:hover:bg-gray-600
                  hover:text-brand transition-all
                  ${isMobileView ? "opacity-100" : "opacity-0 group-hover/feedback:opacity-100"}
                `}
                title="Feedback abgeben"
              >
                <MessageSquarePlus size={14} />
              </button>
            </div>

            {/* Admin Section - innerhalb derselben div für korrektes Alignment */}
            {isAdmin && (
              <>
                <span className="block px-3.5 py-2 mt-2 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#F1C40F' }}>
                  Admin
                </span>
                <SidebarItem
                  icon={<Shield size={18} />}
                  badge={totalAdminNotifications}
                  onClick={() => {
                    // Auf Mobile: Sidebar erst schließen, dann Admin Panel öffnen
                    if (isMobileView) {
                      closeMobileSidebar();
                    }
                    setGmbhFlyoutOpen(false);
                    setAdminFlyoutOpen(true);
                  }}
                  keepSidebarOpen={!isMobileView}
                >
                  Admin Panel
                </SidebarItem>
              </>
            )}

            {/* GmbH Section - für Admin oder Company-Accounts */}
            {showGmbHSection && (
              <>
                <span className="block px-3.5 py-2 mt-2 text-[10px] font-semibold uppercase tracking-widest text-emerald-600 dark:text-emerald-500">
                  GmbH
                </span>
                <SidebarItem
                  icon={<Building2 size={18} />}
                  onClick={() => {
                    if (isMobileView) {
                      closeMobileSidebar();
                    }
                    setAdminFlyoutOpen(false);
                    setGmbhFlyoutOpen(true);
                  }}
                  keepSidebarOpen={!isMobileView}
                  betaBadge={gmbhStatus === 'beta'}
                >
                  GmbH Panel
                </SidebarItem>
              </>
            )}

        </nav>

        {/* Footer (nur Desktop): Avatar/Name/Email fix unten, klickbar zum Profil.
            Hover/Focus über dem Block öffnet darüber ein Flyout mit Theme-Toggle,
            Rollenwechsel (Admin) und Abmelden - gleiche Optik (glass-surface)
            wie das User-Menü auf Mobile. */}
        {!isMobileView && (
          <div
            ref={userMenuContainerRef}
            className="relative flex-shrink-0 px-2 pb-2 pt-2"
            onMouseEnter={openUserMenu}
            onMouseLeave={scheduleCloseUserMenu}
          >
            {/* Flyout: öffnet sich nach oben */}
            <div
              className={`
                glass-surface-soft absolute bottom-full left-2 right-2 mb-2 rounded-2xl p-1 text-sm z-[10000]
                transition duration-150 ease-out origin-bottom
                ${userMenuOpen ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'}
              `}
              onMouseEnter={cancelCloseUserMenu}
              onMouseLeave={scheduleCloseUserMenu}
            >
              {/* Rollen-Badge: aufgeklebt oben links */}
              <span className={`role-badge role-badge-${currentRole}`}>
                <RoleBadgeIcon className="size-[10px]" />
                {roleBadgeLabel}
              </span>

              {/* Header: Theme-Toggle (nur ausserhalb FCMS) */}
              {!location.pathname.startsWith('/fcms') && (
                <>
                  <button
                    onClick={() => setIsDarkMode(!isDarkMode)}
                    aria-label={isDarkMode ? 'Zum Light-Mode wechseln' : 'Zum Dark-Mode wechseln'}
                    className="group flex w-full items-center gap-3 rounded-lg px-3 py-2 hover:bg-gray-100 dark:hover:bg-white/10 cursor-pointer"
                  >
                    <span className={`flex-shrink-0 ${isDarkMode ? 'text-brand' : 'text-gray-500 dark:text-white/60'}`}>
                      {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
                    </span>
                    <span className="flex-1 text-left text-gray-900 dark:text-gray-100">
                      {isDarkMode ? 'Light-Mode' : 'Dark-Mode'}
                    </span>
                  </button>

                  <div className="mx-3 my-1 h-px bg-black/10 dark:bg-white/10" />
                </>
              )}

              {/* Rollen-Switcher (nur Admin) */}
              {isAdmin && (
                <>
                  {([
                    { id: 'admin' as const, label: 'Als Admin anzeigen', icon: <Wrench className="size-4" />, color: 'text-amber-600 dark:text-amber-400' },
                    { id: 'beta' as const, label: 'Als Beta anzeigen', icon: <FlaskConical className="size-4" />, color: 'text-sky-600 dark:text-sky-400' },
                    { id: 'user' as const, label: 'Als Kunde anzeigen', icon: <Crown className="size-4" />, color: 'text-lime-600 dark:text-lime-400' },
                  ]).map((option) => {
                    const isActive = (option.id === 'admin' && user.role === 'admin')
                      || (option.id === 'beta' && user.role === 'user' && user.isBetaTester)
                      || (option.id === 'user' && user.role === 'user' && !user.isBetaTester);
                    return (
                      <button
                        key={option.id}
                        onClick={() => setDemoRole(option.id)}
                        className={`group flex w-full items-center gap-3 rounded-lg px-3 py-2 hover:bg-gray-100 dark:hover:bg-white/10 cursor-pointer ${option.color}`}
                      >
                        {option.icon}
                        <span className="flex-1 text-left">{option.label}</span>
                        {isActive && <span className="text-brand">&#10003;</span>}
                      </button>
                    );
                  })}

                  <div className="mx-3 my-1 h-px bg-black/10 dark:bg-white/10" />
                </>
              )}

              {/* Abmelden */}
              <button
                onClick={handleLogout}
                className="group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 cursor-pointer"
              >
                <LogOut className="size-4" />
                Abmelden
              </button>
            </div>

            {/* Fixer Block unten: Avatar + Name + Email -> Klick navigiert zu /profil */}
            <Link
              to="/profil"
              className="group flex items-center gap-3 rounded-lg p-2 no-underline cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              <UserAvatar user={user} size="sm" />
              <div data-sensitive className="flex flex-col items-start flex-1 min-w-0 overflow-hidden">
                <span className="text-[13px] font-semibold text-gray-800 dark:text-gray-200 whitespace-nowrap overflow-hidden text-ellipsis max-w-[150px] block">
                  {user.firstName || "User"} {user.lastName || ""}
                </span>
                <span className="text-[11px] text-gray-500 dark:text-gray-300 whitespace-nowrap overflow-hidden text-ellipsis max-w-[150px] block">
                  {user.email || user.username || ""}
                </span>
              </div>
            </Link>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      {/* Mobile Trigger Tab - nur unter xl sichtbar, wenn Sidebar geschlossen.
          xl statt lg, damit auch Tablets im Querformat (z.B. iPad Pro 1194px)
          die Off-Canvas-Sidebar mit Rand-Tab bekommen statt der persistenten Desktop-Sidebar. */}
      {!isMobileOpen && (
        <button
          className="
            xl:hidden
            fixed -right-1.5 top-[50%] -translate-y-1/2 z-[9998]
            w-7 h-12 p-0
            rounded-l-lg
            bg-white/85 dark:bg-gray-900/85 backdrop-blur-sm
            border border-r-0 border-gray-200 dark:border-brand
            shadow-[-2px_0_12px_rgba(0,0,0,0.3)]
            flex items-center justify-center
            text-gray-600 dark:text-brand cursor-pointer
            transition-all duration-200
            hover:w-8 hover:bg-gray-200 dark:hover:bg-white/5 hover:text-orange-500 dark:hover:text-brand-hover
          "
          onClick={openMobileSidebar}
          aria-label="Menu öffnen"
        >
          <MenuIcon size={16} />
        </button>
      )}

      {/* Mobile Sidebar - Headless UI Dialog (übernimmt Backdrop, Scroll-Lock,
          Keyboard-Close, Focus-Trap automatisch). Nur unter xl sichtbar,
          damit Tablet-Querformat ebenfalls Off-Canvas nutzt. */}
      <Dialog
        open={isMobileOpen}
        onClose={closeMobileSidebar}
        className="xl:hidden relative z-[9999]"
      >
        {/* Backdrop: fade-in beim Öffnen, fade-out beim Schließen.
            Inline style.transition überschreibt die globale Theme-Transition
            aus indexTW.css (die sonst bei bg-*-Klassen greift und die
            Tailwind-Utility transition-opacity überschreiben würde). */}
        {/* Backdrop ohne backdrop-blur: Auf iPad Safari ist backdrop-filter
            über den gesamten Viewport bei langem Seiteninhalt (z.B.
            Blog-Beitrag) extrem teuer und verursacht spürbares Lag bei der
            Sidebar-Animation. Stattdessen höhere schwarze Opacity und
            will-change Hint für GPU-Compositing. */}
        <DialogBackdrop
          transition
          style={{ transition: 'opacity 300ms ease-out', willChange: 'opacity' }}
          className="fixed inset-0 bg-black/60 data-closed:opacity-0"
        />
        {/* Panel-Wrapper: positioniert das Panel rechts, kein transform hier */}
        <div className="fixed inset-0 flex justify-end pointer-events-none">
          {/* Panel: slide-in von rechts. Inline style.transition ist nötig,
              weil die globale CSS-Regel in indexTW.css (aside/main/bg-gray-*)
              die Tailwind-Utility sonst überschreibt. Tailwind v4 nutzt die
              moderne CSS-Property `translate` (nicht transform:translateX),
              deshalb müssen beide in der transition stehen. */}
          <DialogPanel
            data-lenis-prevent
            transition
            style={{
              transition: 'translate 300ms ease-out, transform 300ms ease-out',
              willChange: 'transform, translate',
            }}
            className={`bg-white dark:bg-gray-900 shadow-[-10px_0_30px_-5px_rgba(0,0,0,0.3),-20px_0_50px_-10px_rgba(0,0,0,0.2)] pointer-events-auto flex flex-col w-full sm:w-80 data-closed:translate-x-full ${className}`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {renderSidebarBody(true)}
          </DialogPanel>
        </div>
      </Dialog>

      {/* Desktop Sidebar - nur ab xl sichtbar, persistent, fix 220px breit.
          xl (>=1280px) statt lg, damit Tablets im Querformat die Off-Canvas-Sidebar bekommen. */}
      <aside
        data-lenis-prevent
        className={clsx(
          "hidden xl:flex fixed top-0 bottom-0 left-0 flex-col z-[9999] w-[220px]",
          "bg-white dark:bg-gray-900",
          "shadow-[4px_0_12px_-2px_rgba(0,0,0,0.1)]",
          className
        )}
      >
        {renderSidebarBody(false)}
      </aside>

      {/* GmbH Flyout Backdrop */}
      {gmbhFlyoutOpen && (
        <div
          className={`
            fixed inset-0 z-[9997] transition-all duration-200
            ${isMobile ? 'bg-black/60 backdrop-blur-sm' : 'bg-black/30'}
          `}
          onClick={() => setGmbhFlyoutOpen(false)}
        />
      )}

      {/* GmbH Flyout - RESPONSIVE: Vollbild auf Mobile, Flyout auf Desktop */}
      {gmbhFlyoutOpen && (
        <div
          className={`
            fixed z-[9998] bg-white dark:bg-gray-900 overflow-y-auto
            ${isMobile
              ? 'inset-0 animate-slide-up-full p-4 pt-6'
              : 'top-0 bottom-0 border-l border-gray-200 dark:border-white/10 shadow-[4px_0_20px_rgba(0,0,0,0.3)] p-6'
            }
          `}
          style={isMobile ? undefined : { left: sidebarWidth }}
        >
          {/* Mobile: Header mit Schließen-Button */}
          {isMobile && (
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200 dark:border-white/10">
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-emerald-500" />
                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                  GmbH Panel
                </span>
              </div>
              <button
                onClick={() => setGmbhFlyoutOpen(false)}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                aria-label="Schließen"
              >
                <X size={20} />
              </button>
            </div>
          )}

          <AdminCardNavTW
            categories={gmbhCategories}
            onNavigate={() => {
              setGmbhFlyoutOpen(false);
              if (isMobile) closeMobileSidebar();
            }}
            inline
            isMobileFullscreen={isMobile}
          />
        </div>
      )}

      {/* Admin Flyout Backdrop - auf Mobile dunkler für besseren Kontrast */}
      {adminFlyoutOpen && (
        <div
          className={`
            fixed inset-0 z-[9997] transition-all duration-200
            ${isMobile ? 'bg-black/60 backdrop-blur-sm' : 'bg-black/30'}
          `}
          onClick={() => setAdminFlyoutOpen(false)}
        />
      )}

      {/* Admin Flyout - RESPONSIVE: Vollbild auf Mobile, Flyout auf Desktop */}
      {adminFlyoutOpen && (
        <div
          className={`
            fixed z-[9998] bg-white dark:bg-gray-900 overflow-y-auto
            ${isMobile
              ? 'inset-0 animate-slide-up-full p-4 pt-6'
              : 'top-0 bottom-0 border-l border-gray-200 dark:border-white/10 shadow-[4px_0_20px_rgba(0,0,0,0.3)] p-6'
            }
          `}
          style={isMobile ? undefined : { left: sidebarWidth }}
        >
          {/* Mobile: Header mit Schließen-Button */}
          {isMobile && (
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200 dark:border-white/10">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-brand" />
                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                  Admin Panel
                </span>
                {totalAdminNotifications > 0 && (
                  <span className="min-w-[22px] h-[22px] px-2 text-xs font-semibold bg-brand text-gray-900 rounded-full flex items-center justify-center">
                    {totalAdminNotifications}
                  </span>
                )}
              </div>
              <button
                onClick={() => setAdminFlyoutOpen(false)}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                aria-label="Schließen"
              >
                <X size={20} />
              </button>
            </div>
          )}

          <AdminCardNavTW
            categories={adminCategories}
            totalNotifications={totalAdminNotifications}
            onNavigate={() => {
              setAdminFlyoutOpen(false);
              // Auf Mobile auch Sidebar schließen
              if (isMobile) closeMobileSidebar();
            }}
            inline
            isMobileFullscreen={isMobile}
          />
        </div>
      )}

      {/* Feedback-Panel (über Sidebar Plus-Button) */}
      <FeedbackSubmissionPanelTW
        isOpen={feedbackPanelOpen}
        onClose={() => setFeedbackPanelOpen(false)}
        onSubmitSuccess={() => {}}
      />
    </>
  );
};

export default SidebarTW;
